import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/feature/PageHeader";
import usePageMeta from "@/hooks/usePageMeta";
import { chapters, chapterList } from "@/mocks/mangaData";

export default function Chapters() {
  usePageMeta({
    title: "甜蜜惩罚漫画分话解析 | 单话详解与关键节点 - 甜蜜惩罚漫画剧情追踪站",
    description:
      "甜蜜惩罚漫画分话解析：逐话拆解关键节点、人物反应与伏笔回收信号，梳理甜蜜惩罚漫画分话主线推进与反转细节，追更剧情不遗漏。",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "甜蜜惩罚漫画分话解析",
        inLanguage: "zh-CN",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "/" },
            { "@type": "ListItem", position: 2, name: "分话解析" },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: chapterList.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `${c.num} ${c.title}`,
        })),
      },
    ],
  });

  const imageMap: Record<string, string> = Object.fromEntries(
    chapters.map((c) => [c.id, c.image]),
  );

  return (
    <Layout>
      <PageHeader
        title="分话解析 · 单话详解"
        subtitle="追踪最新更新，逐话拆解关键节点、人物反应与伏笔回收信号。每话都配有剧情导读与重点回顾，让你追更更省心。"
        crumbs={[
          { label: "首页", path: "/" },
          { label: "分话解析" },
        ]}
      />

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {chapters.map((ch) => (
              <Link
                key={ch.id}
                to={`/chapters/${ch.id}`}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
              >
                <div className="relative h-44 md:h-48 overflow-hidden">
                  <img
                    src={ch.image}
                    alt={`${ch.num} ${ch.title} 分话解析配图`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-foreground-950/70 backdrop-blur text-background-50 px-3 py-1 text-xs font-medium">
                    {ch.num}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-accent-500 text-background-50 px-3 py-1 text-xs font-medium">
                    {ch.tag}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-timer-line"></i>
                    {ch.date}
                  </div>
                  <h2 className="mt-2 font-heading text-lg font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {ch.title}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-foreground-700 line-clamp-2">{ch.summary}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700">
                    阅读本话详解
                    <i className="ri-arrow-right-line text-base leading-none"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-foreground-500 mr-1">更多历史分话持续补全中：</span>
            {["第96-110话", "第60-95话", "第20-59话", "第1-19话"].map((g) => (
              <span
                key={g}
                className="rounded-full bg-background-100 border border-background-200 text-foreground-600 px-3.5 py-1.5 text-xs"
              >
                {g}
              </span>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}